import { api } from './api';
import { avatarCacheService } from './avatarCacheService';
import { imageCacheService } from './imageCacheService';

interface AvatarUploadResponse {
  message: string;
}

interface AvatarUrlResponse {
  avatarUrl: string;
}

class AvatarService {
  /**
   * Faz upload do avatar do usuário
   * @param userId ID do usuário
   * @param avatarFile Arquivo de imagem do avatar
   * @returns Promise com resultado do upload
   */
  async uploadAvatar(userId: string, avatarFile: File): Promise<AvatarUploadResponse> {
    const formData = new FormData();
    formData.append('avatarFile', avatarFile);

    const response = await api.post(`/users/${userId}/avatar`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    // Invalidar cache após upload bem-sucedido
    imageCacheService.removeCachedImage(userId);
    avatarCacheService.removeCachedAvatar(userId);

    return response.data;
  }

  /**
cd   * Obtém a URL do avatar do usuário
   * @param userId ID do usuário
   * @param useDirectUrl Se deve usar URL direta da API (padrão: true)
   * @returns URL do avatar ou null se não existir
   */
  async getAvatarUrl(userId: string, useDirectUrl: boolean = true): Promise<string | null> {
    try {
      if (useDirectUrl) {
        // Usar URL direta da API (mais eficiente e confiável)
        const directUrl = this.getAvatarFileUrl(userId);
        
        // Verificar se a imagem existe fazendo uma requisição HEAD para o endpoint /avatar/file
        try {
          const response = await api.head(`/users/${userId}/avatar/file`);
          if (response.status === 200) {
            return directUrl;
          }
        } catch {
          // Erro na requisição, avatar não existe
        }
        
        return null;
      } else {
        // Método antigo com blob URLs (mantido para compatibilidade)
        const cachedImageUrl = imageCacheService.getCachedImage(userId);
        if (cachedImageUrl) {
          return cachedImageUrl;
        }

        const response = await api.get(`/users/${userId}/avatar/file`, {
          responseType: 'blob'
        });

        if (!response.data) {
          return null;
        }

        const blobUrl = URL.createObjectURL(response.data);
        imageCacheService.cacheImage(userId, blobUrl);
        return blobUrl;
      }
    } catch (error) {
      console.error('Erro ao obter avatar:', error);
      return null;
    }
  }

  /**
   * Obtém a URL do arquivo de avatar da API
   * @param userId ID do usuário
   * @param versionId ID de versão para cache-busting (opcional)
   * @returns URL do arquivo de avatar
   */
  getAvatarFileUrl(userId: string, versionId?: string): string {
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    let url = `${baseUrl}/users/${userId}/avatar/file`;
    
    // Adicionar parâmetro de cache-busting se fornecido
    if (versionId) {
      url += `?v=${versionId}`;
    }
    
    return url;
  }

  /**
   * Remove o avatar do usuário
   * @param userId ID do usuário
   * @returns Promise com resultado da remoção
   */
  async removeAvatar(userId: string): Promise<{ message: string }> {
    const response = await api.delete(`/users/${userId}/avatar`);

    // Invalidar cache após remoção bem-sucedida
    imageCacheService.removeCachedImage(userId);
    avatarCacheService.removeCachedAvatar(userId);

    return response.data;
  }

  /**
   * Valida se o arquivo é uma imagem válida
   * @param file Arquivo a ser validado
   * @returns true se válido, false caso contrário
   */
  validateImageFile(file: File): { isValid: boolean; error?: string } {
    // Validar tipo de arquivo
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      return {
        isValid: false,
        error: 'Tipo de arquivo não suportado. Use JPG, JPEG ou PNG'
      };
    }

    // Validar tamanho (máximo 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      return {
        isValid: false,
        error: 'Arquivo muito grande. Tamanho máximo: 5MB'
      };
    }

    return { isValid: true };
  }
}

export const avatarService = new AvatarService();
